import { ReloadOutlined } from "@ant-design/icons";
import { Button, DatePicker, Select, Spin, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import CommonIcons from "../../../../assets/icons";
import { OrderItem } from "../../../../components/VMRS/Order/Tasking/types/order";
import { useFetchDataRealtimeSync } from "../../../../components/VMRS/Order/hooks/useFetchDataRealtime";
import ContentLayoutManagement from "../../../../layouts/ContentLayoutManagement";
import HeaderContent from "../../../../layouts/HeaderContent";
import { getOrderAsync } from "../../../../store/reducers/orderSlice";
import { RootState } from "../../../../store/store";
import { getArchiveProviders } from "../../../../services/mapServices";
import { sumColumnWidths } from "../../../../components/VMRS/Order/Tasking/ultis";

const ALL_VALUE = "all";

export default function ArchiveOrderListPage()
{
   const navigate = useNavigate();
   const dispatch = useDispatch();
   const { t } = useTranslation();
   const [searchParams, setSearchParams] = useSearchParams();

   const [data, setData] = useState<OrderItem[]>([]);
   const [total, setTotal] = useState(0);
   const [loading, setLoading] = useState(false);

   const notifications = useSelector(
      (state: RootState) => state.notificationSlice.total,
   );

   // Hãng lọc đơn: BE lọc theo nhóm user (scope), chỉ lấy hãng đang hoạt động.
   const [providers, setProviders] = useState<any[]>([]);
   useEffect(() =>
   {
      let cancelled = false;
      getArchiveProviders()
         .then((res: any) =>
         {
            if (cancelled) return;
            setProviders(
               (res?.data?.data ?? []).filter(
                  (item: any) => item?.is_active && item?.is_archive,
               ),
            );
         })
         .catch((e: any) =>
         {
            console.error(e);
            if (!cancelled) setProviders([]);
         });
      return () =>
      {
         cancelled = true;
      };
   }, []);

   const dataStatusOrder = useSelector(
      (state: RootState) => state.appCommonSlice.dataStatusOrder,
   );

   // ================= PARSE URL =================
   const parseFiltersFromURL = (params: URLSearchParams) =>
   {
      return {
         page: Number(params.get("page") || 0),
         pageSize: Number(params.get("size") || 10),
         sort: (params.get("sort") as "asc" | "desc") || "desc",
         providerIds: params.get("providers")
            ? params.get("providers")!.split(",")
            : ["all"],
         statusIds: params.get("statusIds")
            ? params.get("statusIds")!.split(",")
            : ["all"],
         startDate: params.get("startDate")
            ? Number(params.get("startDate"))
            : null,
         endDate: params.get("endDate") ? Number(params.get("endDate")) : null,
         searchName: params.get("name") || "",
      };
   };

   // ================= STATE =================
   const [filters, setFilters] = useState(() =>
      parseFiltersFromURL(searchParams),
   );

   const {
      page,
      pageSize,
      sort,
      providerIds,
      statusIds,
      startDate,
      endDate,
      searchName,
   } = filters;

   // ================= SYNC URL =================

   useEffect(() =>
   {
      const params = new URLSearchParams();

      if (filters.page > 0) params.set("page", String(filters.page));
      if (filters.pageSize !== 10) params.set("size", String(filters.pageSize));
      if (filters.sort !== "desc") params.set("sort", filters.sort);

      if (!filters.providerIds.includes("all"))
      {
         params.set("providers", filters.providerIds.join(","));
      }

      if (!filters.statusIds.includes("all"))
      {
         params.set("statusIds", filters.statusIds.join(","));
      }

      if (filters.startDate) params.set("startDate", String(filters.startDate));
      if (filters.endDate) params.set("endDate", String(filters.endDate));
      if (filters.searchName) params.set("name", filters.searchName);

      setSearchParams(params, { replace: true });
   }, [filters]);

   const updateFilters = (newPartial: Partial<typeof filters>) =>
   {
      setFilters((prev) => ({
         ...prev,
         ...newPartial,
         page: 0,
      }));
   };

   // ================= API =================
   const getData = async () =>
   {
      try
      {
         setLoading(true);

         // @ts-ignore
         const res = await dispatch(
            // @ts-ignore
            getOrderAsync({
               order_type: "archive",
               page: page,
               limit: pageSize,
               sort,
               provider_ids: providerIds.includes("all") ? undefined : providerIds,
               status_ids: statusIds?.includes("all") ? undefined : statusIds,
               start_date: startDate ? Math.floor(startDate / 1000) : undefined,
               end_date: endDate ? Math.floor(endDate / 1000) : undefined,
               name: searchName || undefined,
            }),
         ).unwrap();

         setData(res?.data?.data || []);
         setTotal(res?.data?.pagination?.total || 0);
      } catch
      {
         setData([]);
         setTotal(0);
      } finally
      {
         setLoading(false);
      }
   };

   useEffect(() =>
   {
      getData();
   }, [
      page,
      pageSize,
      sort,
      startDate,
      endDate,
      searchName,
      providerIds.join(","),
      statusIds.join(","),
   ]);

   // ================= HANDLE PROVIDER AND STATUS =================

   const handleProviderChange = (vals: string[]) =>
   {
      if (!providers) return;

      const allIds = providers.map((p) => p.id.toString());

      const hasAll = vals.includes(ALL_VALUE);

      // Case 1: click ALL từ trạng thái không ALL
      if (hasAll && !providerIds.includes(ALL_VALUE))
      {
         updateFilters({ providerIds: [ALL_VALUE, ...allIds] });
         return;
      }

      // Case 2: đang ALL mà user bỏ bớt item
      if (providerIds.includes(ALL_VALUE) && hasAll)
      {
         const cleaned = vals.filter((v) => v !== ALL_VALUE);

         if (cleaned.length !== allIds.length)
         {
            updateFilters({ providerIds: cleaned });
            return;
         }

         updateFilters({ providerIds: [ALL_VALUE, ...allIds] });
         return;
      }

      // Case 3: select bình thường
      const cleaned = vals.filter((v) => v !== ALL_VALUE);

      if (cleaned.length === allIds.length)
      {
         updateFilters({ providerIds: [ALL_VALUE, ...allIds] });
         return;
      }
      updateFilters({ providerIds: cleaned });
   };

   const handleStatusChange = (vals: string[]) =>
   {
      if (!dataStatusOrder) return;

      const allIds = dataStatusOrder.map((s: any) => s.id.toString());

      const prevHasAll = statusIds.includes(ALL_VALUE);
      const hasAll = vals.includes(ALL_VALUE);

      // Case 1: click ALL (bật ALL)
      if (!prevHasAll && hasAll)
      {
         updateFilters({ statusIds: [ALL_VALUE, ...allIds] });
         return;
      }

      // Case 2: bỏ ALL
      if (prevHasAll && !hasAll)
      {
         const cleaned = vals.filter(v => v !== ALL_VALUE);
         updateFilters({ statusIds: cleaned });
         return;
      }

      // Case 3: bình thường
      const cleaned = vals.filter(v => v !== ALL_VALUE);

      if (cleaned.length === allIds.length)
      {
         updateFilters({ statusIds: [ALL_VALUE, ...allIds] });
         return;
      }

      updateFilters({ statusIds: cleaned });
   };

   // ================= RESET =================
   const handleResetFilters = () =>
   {
      const { origin, pathname } = window.location;
      const fullPath = origin + pathname;
      const currentHref = window.location.href;

      if (currentHref === fullPath)
      {
         getData();
         return;
      }
      setFilters({
         page: 0,
         pageSize: 10,
         sort: "desc",
         providerIds: ["all"],
         statusIds: ["all"],
         startDate: null,
         endDate: null,
         searchName: "",
      });
   };

   const columns: ColumnsType<OrderItem> = [
      {
         title: t("No."),
         key: "index",
         width: 70,
         render: (_: any, __: any, index: number) => page * pageSize + index + 1,
         align: "center",
      },
      {
         title: t("Order Name"),
         dataIndex: "name",
         key: "name",
         width: 220,
      },
      {
         title: t("Provider"),
         key: "provider",
         render: (item: OrderItem) =>
         {
            const names: string[] = (item?.items || [])
               .map((i: any) => i?.provider?.name)
               .filter(
                  (name: string, index: number, self: string[]) =>
                     name && self.indexOf(name) === index,
               );

            if (names.length === 0) return <span>N/A</span>;

            const visible = names.slice(0, 3);
            const rest = names.length - visible.length;

            const content = (
               <span className="flex items-center gap-1">
                  <span>{visible.join(", ")}</span>
                  {rest > 0 && <span className="cursor-default">+{rest}</span>}
               </span>
            );

            if (rest > 0)
            {
               return (
                  <Tooltip
                     styles={{ root: { pointerEvents: "none" } }}
                     title={names.join(", ")}
                  >
                     {content}
                  </Tooltip>
               );
            }

            return content;
         },
         width: 300,
      },
      {
         title: t("Ordered at"),
         dataIndex: "created_at",
         key: "created_at",
         render: (timestamp: number) =>
            dayjs(timestamp).format("HH:mm DD/MM/YYYY"),
         width: 220,
         sorter: true,
      },
      {
         title: t("Status"),
         key: "status",
         render: (item: OrderItem) => (
            <Tag
               color={item?.status?.color}
               className="px-3 py-1 rounded-full !text-white !min-w-[150px] flex !justify-center"
            >
               {t(item?.status?.name)}
            </Tag>
         ),
         width: 200,
      },
      {
         title: t("Priority"),
         key: "priority",
         render: (item: OrderItem) => <p>{t(item?.priority)}</p>,
         width: 150,
         align: "center",
      },
      {
         title: "",
         key: "action",
         align: "right",
         render: (_: any, record: any) => (
            <div className="flex items-center text-center  justify-end">
               <Button
                  type="link"
                  onClick={() =>
                     navigate({
                        pathname: `./${record.id}`,
                        search: searchParams.toString(),
                     })
                  }
               >
                  {t("Detail")}
               </Button>

               <Button
                  className="!w-6 min-w-6 h-6"
                  type="text"
                  icon={<CommonIcons.NextIcon />}
                  onClick={() =>
                     navigate({
                        pathname: `./${record.id}`,
                        search: searchParams.toString(),
                     })
                  }
               />
            </div>
         ),
         width: 200,
      },
   ];

   useFetchDataRealtimeSync({
      notifications,
      getData,
      path: `order/management-order`,
   });

   const handleTableChange = (pagination: any, _: any, sorter: any) =>
   {
      const s = Array.isArray(sorter) ? sorter[0] : sorter;

      setFilters((prev) => ({
         ...prev,
         page: pagination.current - 1,
         pageSize: pagination.pageSize,
         sort: s?.order ? (s.order === "ascend" ? "asc" : "desc") : prev.sort,
      }));
   };

   // ================= RENDER =================
   return (
      <ContentLayoutManagement
         header={<HeaderContent breadcrumb={[{ title: t("Archive Orders") }]} />}
      >
         <Spin spinning={loading}>
            <div className="p-6 bg-[#0f172a] h-full overflow-hidden text-white flex flex-col">
               {/* Filters */}
               <div className="flex flex-wrap gap-3 mb-4 w-full">
                  <div className="flex-1 min-w-[200px]">
                     <p style={{ fontSize: "14px" }} className="mb-1">
                        {t("Provider")}
                     </p>
                     <Select
                        disabled={loading}
                        mode="multiple"
                        value={providerIds}
                        onChange={handleProviderChange}
                        optionFilterProp="label"
                        className="div-dropdown"
                        options={[{ value: "all", label: t("All") }].concat(
                           providers?.map((provider) => ({
                              value: provider.id.toString(),
                              label: provider.name,
                           })) || [],
                        )}
                        tagRender={(props) =>
                        {
                           const isAllSelected =
                              providerIds.includes("all") ||
                              providerIds.length === providers?.length;

                           if (isAllSelected)
                           {
                              if (props.value !== "all")
                              {
                                 return <span style={{ display: "none" }} />;
                              }

                              return (
                                 <Tag className="tag-select" closable={false}>
                                    {t("All")}
                                 </Tag>
                              );
                           }

                           return (
                              <Tag
                                 className="tag-select"
                                 closable
                                 onClose={props.onClose}
                              >
                                 {props.label}
                              </Tag>
                           );
                        }}
                     />
                  </div>

                  <div className="flex-1 min-w-[200px]">
                     <p className="mb-1" style={{ fontSize: "14px" }}>
                        {t("Status")}
                     </p>

                     <Select
                        disabled={loading}
                        mode="multiple"
                        value={statusIds}
                        maxTagCount="responsive"
                        maxTagPlaceholder={(omittedValues) => (
                           <Tooltip
                              styles={{ root: { pointerEvents: 'none' } }}
                              title={omittedValues.map(({ label }) => label).join(', ')}
                           >
                              <span>+ {omittedValues?.length}...</span>
                           </Tooltip>
                        )}
                        onChange={handleStatusChange}
                        optionFilterProp="label"
                        className="div-dropdown"
                        options={[{ value: "all", label: t("All") }].concat(
                           dataStatusOrder?.map((item: any) => ({
                              value: item.id.toString(),
                              label: t(item.name),
                           })) || [],
                        )}
                        tagRender={(props) =>
                        {
                           const isAllSelected =
                              statusIds.includes("all") ||
                              statusIds.length === dataStatusOrder?.length;

                           if (isAllSelected)
                           {
                              if (props.value !== "all")
                              {
                                 return <span style={{ display: "none" }} />;
                              }

                              return (
                                 <Tag className="tag-select" closable={false}>
                                    {t("All")}
                                 </Tag>
                              );
                           }

                           return (
                              <Tag
                                 className="tag-select"
                                 closable
                                 onClose={props.onClose}
                              >
                                 {props.label}
                              </Tag>
                           );
                        }}
                     />
                  </div>

                  <div className="flex-1 min-w-[320px]">
                     <p className="mb-1" style={{ fontSize: "14px" }}>
                        {t("Created at")}
                     </p>
                     <div className="flex gap-3">
                        <DatePicker
                           value={startDate ? dayjs(startDate) : null}
                           onChange={(date) =>
                              updateFilters({
                                 startDate: date ? date.startOf("day").valueOf() : null,
                              })
                           }
                           placeholder={t("Start date")}
                           className=" div-dropdown"
                           format="DD/MM/YYYY"
                           disabledDate={(current) =>
                           {
                              if (!current) return false;

                              const isFuture = current > dayjs().endOf("day");
                              const isAfterEnd =
                                 !!endDate && current > dayjs(endDate).endOf("day");

                              return isFuture || isAfterEnd;
                           }}
                           disabled={loading}
                        />
                        <DatePicker
                           value={endDate ? dayjs(endDate) : null}
                           onChange={(date) =>
                           {
                              if (!date)
                              {
                                 setFilters((prev) => ({
                                    ...prev,
                                    endDate: null,
                                    page: 0,
                                 }));
                                 return;
                              }

                              const isToday = date.isSame(dayjs(), "day");
                              updateFilters({
                                 endDate: isToday
                                    ? dayjs().valueOf()
                                    : date.endOf("day").valueOf(),
                              });
                           }}
                           placeholder={t("End date")}
                           className="div-dropdown"
                           format="DD/MM/YYYY"
                           disabledDate={(current) =>
                           {
                              if (!current) return false;

                              const isFuture = current > dayjs().endOf("day");
                              const isBeforeStart =
                                 !!startDate && current < dayjs(startDate).startOf("day");

                              return isFuture || isBeforeStart;
                           }}
                           disabled={loading}
                        />
                     </div>
                  </div>

                  <div className="min-w-max flex flex-col mb-1">
                     <p className="mb-1">{t("Reset")}</p>
                     <Button
                        disabled={loading}
                        className="!bg-[var(--neutral-900)]  !border-none text-[var(--text-primary)]"
                        icon={<ReloadOutlined />}
                        onClick={handleResetFilters}
                     >
                        {t("Reset")}
                     </Button>
                  </div>
               </div>

               {/* Table */}

               <Table
                  className="flex-1 min-h-0"
                  columns={columns}
                  // Below the width the columns ask for, scroll sideways rather
                  // than squeeze every cell down to a few characters.
                  scroll={{ x: sumColumnWidths(columns) }}
                  tableLayout="fixed"
                  dataSource={data}
                  pagination={{
                     current: page + 1,
                     pageSize: pageSize,
                     total: total,
                     showTotal: (total, range) =>
                        // One sentence, one key: the old version translated only
                        // "Show" and "records", leaving "to"/"of" in English.
                        t("Showing {{from}} to {{to}} of {{total}} records", {
                           from: range[0],
                           to: range[1],
                           total,
                        }),
                     pageSizeOptions: ["10", "20", "50", "100"],
                  }}
                  onRow={(record) =>
                  {
                     return {
                        onClick: () =>
                        {
                           navigate({
                              pathname: `./${record.id}`,
                              search: searchParams.toString(),
                           });
                        },
                     };
                  }}
                  onChange={handleTableChange}
               />
            </div>
         </Spin>
      </ContentLayoutManagement>
   );
}
